"use client";

import type { ReactNode } from "react";
import { BUTTON_SMALL, INPUT, TD, TH } from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";
import type {
  MatrixColumn,
  MatrixRow,
} from "@/lib/tool-kit/research-workflow/lit-matrix";

/*
 * The editable grid of the literature review matrix: one row per source, one cell per
 * column, every cell a labelled text box. Split from the widget so the widget keeps its
 * strings, state and toolbar; every word here comes from those strings via `labels`.
 */

export interface MatrixGridLabels {
  caption: string;
  headers: { author: string; year: string; title: string; themes: string };
  actionsHeader: string;
  removeSource: string;
  /** With `{source}`. */
  removeSourceLabel: string;
  /** With `{number}`. */
  untitledSource: string;
  /** With `{column}` and `{source}`. */
  cellLabel: string;
  authorPlaceholder: string;
  yearPlaceholder: string;
  titlePlaceholder: string;
  themesPlaceholder: string;
}

const CELL_INPUT = `${INPUT} min-w-[9rem]`;
const CELL_AREA = `${INPUT} min-h-[4.5rem] min-w-[13rem] resize-y leading-relaxed`;

function sourceName(
  row: MatrixRow,
  position: string,
  labels: MatrixGridLabels
) {
  const author = row.author.trim();
  const year = row.year.trim();
  if (author) {
    return year ? `${author} (${year})` : author;
  }
  return row.title.trim() || t(labels.untitledSource, { number: position });
}

function Cell({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <td className={`${TD} align-top`}>
      <label className="sr-only" htmlFor={id}>
        {label}
      </label>
      {children}
    </td>
  );
}

export function MatrixGrid({
  rows,
  columns,
  positions,
  labels,
  onField,
  onCell,
  onTags,
  onRemove,
}: {
  rows: readonly MatrixRow[];
  columns: readonly MatrixColumn[];
  /** Each row's number in the matrix, already formatted, by row id. */
  positions: ReadonlyMap<string, string>;
  labels: MatrixGridLabels;
  onField: (
    id: string,
    field: "author" | "year" | "title",
    value: string
  ) => void;
  onCell: (id: string, column: string, value: string) => void;
  /** The tags as typed; the widget parses them. */
  onTags: (id: string, text: string) => void;
  onRemove: (id: string) => void;
}) {
  const tagText = (row: MatrixRow) => row.tags.join(", ");
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className="w-full border-collapse text-left text-sm">
        <caption className="sr-only">{labels.caption}</caption>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {labels.headers.author}
            </th>
            <th className={TH} scope="col">
              {labels.headers.year}
            </th>
            <th className={TH} scope="col">
              {labels.headers.title}
            </th>
            {columns.map((column) => (
              <th className={TH} key={column.id} scope="col">
                {column.label}
              </th>
            ))}
            <th className={TH} scope="col">
              {labels.headers.themes}
            </th>
            <th className={`${TH} print:hidden`} scope="col">
              {labels.actionsHeader}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const name = sourceName(row, positions.get(row.id) ?? "", labels);
            const label = (column: string) =>
              t(labels.cellLabel, { column, source: name });
            return (
              <tr key={row.id}>
                <Cell
                  id={`lrm-${row.id}-author`}
                  label={label(labels.headers.author)}
                >
                  <input
                    className={CELL_INPUT}
                    id={`lrm-${row.id}-author`}
                    onChange={(event) =>
                      onField(row.id, "author", event.target.value)
                    }
                    placeholder={labels.authorPlaceholder}
                    type="text"
                    value={row.author}
                  />
                </Cell>
                <Cell
                  id={`lrm-${row.id}-year`}
                  label={label(labels.headers.year)}
                >
                  <input
                    className={`${INPUT} min-w-[5.5rem]`}
                    id={`lrm-${row.id}-year`}
                    inputMode="numeric"
                    onChange={(event) =>
                      onField(row.id, "year", event.target.value)
                    }
                    placeholder={labels.yearPlaceholder}
                    type="text"
                    value={row.year}
                  />
                </Cell>
                <Cell
                  id={`lrm-${row.id}-title`}
                  label={label(labels.headers.title)}
                >
                  <textarea
                    className={CELL_AREA}
                    id={`lrm-${row.id}-title`}
                    onChange={(event) =>
                      onField(row.id, "title", event.target.value)
                    }
                    placeholder={labels.titlePlaceholder}
                    value={row.title}
                  />
                </Cell>
                {columns.map((column) => (
                  <Cell
                    id={`lrm-${row.id}-${column.id}`}
                    key={column.id}
                    label={label(column.label)}
                  >
                    <textarea
                      className={CELL_AREA}
                      id={`lrm-${row.id}-${column.id}`}
                      onChange={(event) =>
                        onCell(row.id, column.id, event.target.value)
                      }
                      value={row.cells[column.id] ?? ""}
                    />
                  </Cell>
                ))}
                <Cell
                  id={`lrm-${row.id}-themes`}
                  label={label(labels.headers.themes)}
                >
                  <input
                    className={CELL_INPUT}
                    defaultValue={tagText(row)}
                    id={`lrm-${row.id}-themes`}
                    key={tagText(row)}
                    onBlur={(event) => onTags(row.id, event.target.value)}
                    placeholder={labels.themesPlaceholder}
                    type="text"
                  />
                </Cell>
                <td className={`${TD} align-top print:hidden`}>
                  <button
                    aria-label={t(labels.removeSourceLabel, { source: name })}
                    className={BUTTON_SMALL}
                    onClick={() => onRemove(row.id)}
                    type="button"
                  >
                    {labels.removeSource}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
